// Claude: AdminBuyerSubscriptionSection — read-only display: plan details, payment records, notes 21-04-2026
// Claude: pricePerRFX → subscriptionFee; statements from buyer_statements 25-Apr-2026
import { Box, Flex, Text } from "@chakra-ui/react";
import { Table } from "antd";
import React, { useEffect, useState } from "react";
import { AdminBuyerAPI } from "../../../Apis/adminBuyerAPI";
import { useToastWrapper } from "../../../Wrapper/toastWrapper";
import { errorHandler } from "../../../Utils/handler";
import moment from "moment-timezone";

interface Props {
  buyerId: string;
  selectedPlanId?: string;
  plansData?: any[];
}

const AdminBuyerSubscriptionSection = ({ buyerId, selectedPlanId, plansData = [] }: Props) => {
  const { error } = useToastWrapper();
  const [buyerData, setBuyerData] = useState<any>(null);
  const [statements, setStatements] = useState<any[]>([]);
  const fetchData = async () => {
    try {
      const [buyerRes, statementsRes] = await Promise.all([
        AdminBuyerAPI.getBuyerById(buyerId),
        AdminBuyerAPI.getStatements(buyerId),
      ]);
      setBuyerData(buyerRes?.data?.data);
      setStatements(statementsRes?.data?.data ?? []);
    } catch (err) {
      error(errorHandler(err));
    }
  };

  useEffect(() => { fetchData(); }, [buyerId]);

  // Claude: resolve plan from dropdown selection in real-time — no save required 20-Apr-2026
  const plan = plansData.find((p: any) => p._id === selectedPlanId) ?? null;
  const currency = plan?.currency ?? "";

  const paymentColumns = [
    { title: "Date", dataIndex: "date", key: "date", render: (v: any) => v ? moment(v).format("DD/MM/YYYY") : "—" },
    { title: "Plan", dataIndex: "planName", key: "planName" },
    { title: "Plan Amount", dataIndex: "planAmount", key: "planAmount", render: (v: any, r: any) => `${r.currency ?? currency} ${v ?? 0}` },
    { title: "Credits Used", dataIndex: "creditsUtilized", key: "creditsUtilized", render: (v: any) => v ?? 0 },
    { title: "Paid Amount", dataIndex: "paidAmount", key: "paidAmount", render: (v: any, r: any) => `${r.currency ?? currency} ${v ?? 0}` },
    { title: "Reference", dataIndex: "paymentReference", key: "paymentReference" },
    { title: "RFP Added", dataIndex: "rfxAdded", key: "rfxAdded" },
  ];

  if (!buyerData) return null;

  const paymentRows = statements.filter((r: any) => r.recordType === 'statement');

  return (
    <Box>
      {/* Plan Details — Claude: correct buyer plan fields 25-Apr-2026 */}
      <Box mb={3}>
        <Flex gap={6} flexWrap="wrap">
          <Text fontSize="xs"><b>Plan:</b> {plan?.planName ?? "—"}</Text>
          <Text fontSize="xs"><b>Subscription Fee:</b> {currency} {plan?.subscriptionFee ?? "—"}</Text>
          <Text fontSize="xs"><b>RFP Limit:</b> {plan?.rfxLimit ?? "—"}</Text>
          <Text fontSize="xs"><b>MAR:</b> {plan?.minAssuredResponses ?? "—"}</Text>
          {plan?.pendingMeetingsLimit != null && (
            <Text fontSize="xs"><b>Meetings Limit:</b> {plan.pendingMeetingsLimit || "∞"}</Text>
          )}
          {plan?.rfpResponsePendingLimit != null && (
            <Text fontSize="xs"><b>Response Pending Limit:</b> {plan.rfpResponsePendingLimit || "∞"}</Text>
          )}
          <Text fontSize="xs">
            <b>Plan Active:</b>{" "}
            <Text as="span" color={buyerData.isPlanActive ? "green.600" : "red.500"}>
              {buyerData.isPlanActive ? "Yes" : "No"}
            </Text>
          </Text>
        </Flex>
      </Box>

      {/* Payment Records */}
      <Box mb={3}>
        <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={1}>
          Payment Records
        </Text>
        <Table
          size="small"
          columns={paymentColumns}
          dataSource={paymentRows.map((r: any, i: number) => ({ ...r, key: i }))}
          pagination={false}
          locale={{ emptyText: "No payment records" }}
        />
      </Box>

      {/* Subscription Notes */}
      {(buyerData.subscriptionNotes ?? []).length > 0 && (
        <Box>
          <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={1}>
            Subscription Notes
          </Text>
          <Box maxH="120px" overflowY="auto" fontSize="xs" color="gray.600" bg="gray.50" p={2} borderRadius="md">
            {(buyerData.subscriptionNotes ?? []).map((note: string, i: number) => (
              <Text key={i} mb={1}>{note}</Text>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AdminBuyerSubscriptionSection;
